import React, { useEffect, useState } from "react";

import {
  bannerKey,
  clearLocalKlimVar,
  getLocalKlimVar,
  isPercentageComplete,
  setLocalKlimVar,
  useGetCarbonProgramByFarmId,
  useGetFootprintingBeefFootprint,
  useGetFootprintingCropAssessmentsRequiringReview,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprint,
  useGetFootprintingPigletFootprint,
  useGetFootprintingPorkFootprint,
  useGetPracticeCrops,
  useMutateFootprintingBeefPrimaryData,
  useMutateFootprintingCropCreateManyBlankPrimaryData,
  useMutateFootprintingDairyPrimaryData,
  useMutateFootprintingPigletPrimaryData,
  useMutateFootprintingPorkPrimaryData,
  useTranslation,
} from "utils";

import { FOOTPRINTING_BANNER_KEYS } from "models";

import { AlertBanner, ErrorBoundary, Flex, Icon, IconButton, Modal, SelectableImageCard, Typography } from "components";

import { TaskOverviewDrawer } from "pages/app/farm/components/TaskOverviewDrawer";
import { AssessmentSelectionScreen } from "pages/app/footprinting/components/FootprintingAssessmentSelectionScreen";
import FootprintingFormButton, { CARD_TYPE_ICON, FootprintingLoadingState } from "pages/app/footprinting/components/FootprintingFormButton";

interface BannerConfiguration {
  iconName: string;
  type: string;
  titleKey?: string;
  descriptionKey?: string;
  link?: boolean;
  linkTextKey?: string;
  buttonTextKey?: string;
  dismissButtonTextKey?: string;
  hasClick?: boolean;
}

const BANNER_CONFIGS: Record<string, BannerConfiguration> = {
  FIRST_COMPLETE: {
    iconName: "tabler/confetti",
    type: "success",
    titleKey: "footprintingAlertBanner.firstFootprintTitle",
    descriptionKey: "footprintingAlertBanner.firstFootprintDescription",
  },
  MULTIPLE_COMPLETE: {
    iconName: "tabler/confetti",
    type: "success",
    titleKey: "footprintingAlertBanner.multipleFootprintTitle",
    descriptionKey: "footprintingAlertBanner.multipleFootprintDescription",
  },
  ALL_COMPLETE: {
    iconName: "tabler/confetti",
    type: "success",
    titleKey: "footprintingAlertBanner.allAssessmentsCompletedTitle",
    descriptionKey: "footprintingAlertBanner.allAssessmentsCompletedDescription",
  },
  AUTOFILL_SUCCESSFUL: {
    iconName: "tabler/confetti",
    type: "info-highlighted",
    descriptionKey: "footprintingAlertBanner.autofillSuccessfulDescription",
  },
  VERIFICATION: {
    iconName: "tabler/info-circle",
    type: "info",
    titleKey: "footprintingAlertBanner.awaitingVerificationTitle",
    descriptionKey: "footprintingAlertBanner.awaitingVerificationDescription",
    link: true,
    linkTextKey: "footprintingAlertBanner.awaitingVerificationLink",
    hasClick: true,
  },
  UNSUBMITTED_AFFECTED_BY_SPLIT: {
    iconName: "tabler/alert-triangle",
    type: "error",
    titleKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.header",
    descriptionKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.description",
    buttonTextKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.reviewButton",
    dismissButtonTextKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.dismissButton",
  },
  SUBMITTED_AFFECTED_BY_SPLIT: {
    iconName: "tabler/info-circle",
    type: "info",
    titleKey: "assessmentManagement.submittedAssessmentsAffectedBySplitBanner.header",
    descriptionKey: "assessmentManagement.submittedAssessmentsAffectedBySplitBanner.description",
  },
} as const;

export const FootprintingYearPage = ({
  year,
  farmId,
  cropsWithFootprintForYear,
  yearIsSubmitted,
}: {
  year: number;
  farmId: string;
  cropsWithFootprintForYear: string[];
  yearIsSubmitted: boolean;
}) => {
  const { t } = useTranslation("footprinting");
  const [showTaskOverviewDrawer, setShowTaskOverviewDrawer] = useState(false);
  const [showAddAssessmentModal, setShowAddAssessmentModal] = useState(false);
  const [bannerConfig, setBannerConfig] = useState<(typeof BANNER_CONFIGS)[keyof typeof BANNER_CONFIGS] | null>(null);
  const [unsubmittedSplitBannerConfig, setUnsubmittedSplitBannerConfig] = useState<(typeof BANNER_CONFIGS)["UNSUBMITTED_AFFECTED_BY_SPLIT"] | null>(null);
  const [submittedSplitBannerConfig, setSubmittedSplitBannerConfig] = useState<(typeof BANNER_CONFIGS)["SUBMITTED_AFFECTED_BY_SPLIT"] | null>(null);
  const { data: allCrops } = useGetPracticeCrops({ enabled: true });
  const [cropAssessmentToReview, setCropAssessmentToReview] = useState<{ cropId: string; step: number } | null>(null);
  const carbonProgram = useGetCarbonProgramByFarmId(farmId);
  const beefEnabled = carbonProgram.data?.platformFeatures.footprinting?.beef === true;
  const dairyEnabled = carbonProgram.data?.platformFeatures.footprinting?.dairy === true;
  const porkEnabled = carbonProgram.data?.platformFeatures.footprinting?.pork === true;

  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year], cropsWithFootprintForYear);
  const beefProgressQuery = useGetFootprintingBeefFootprint(farmId, year, { enabled: beefEnabled });
  const dairyProgressQuery = useGetFootprintingDairyFootprint(farmId, year, { enabled: dairyEnabled });
  const pigletProgressQuery = useGetFootprintingPigletFootprint(farmId, year, { enabled: porkEnabled });
  const porkProgressQuery = useGetFootprintingPorkFootprint(farmId, year, { enabled: porkEnabled });
  const { data: assessmentsRequiringReview, isLoading: isAssessmentsRequiringReviewLoading } = useGetFootprintingCropAssessmentsRequiringReview({
    enabled: true,
  });

  const allAssessmentsRequiringReviewForCurrentYear = Object.entries(assessmentsRequiringReview?.[year] ?? {});
  const submittedAssessmentsRequiringReviewForCurrentYear = allAssessmentsRequiringReviewForCurrentYear.filter(([_, assessment]) => assessment.isSubmitted);
  const unsubmittedAssessmentsRequiringReviewForCurrentYear = allAssessmentsRequiringReviewForCurrentYear.filter(([_, assessment]) => !assessment.isSubmitted);

  const [isMutating, setIsMutating] = useState(false);
  const [selectedBeef, setSelectedBeef] = useState<boolean>(false);
  const [selectedDairy, setSelectedDairy] = useState<boolean>(false);
  const [selectedPiglet, setSelectedPiglet] = useState<boolean>(false);
  const [selectedPork, setSelectedPork] = useState<boolean>(false);
  const [selectedCommodities, setSelectedCommodities] = useState<Record<string, boolean>>({});
  const { mutateAsync: createBlankBeefPrimaryDataRow } = useMutateFootprintingBeefPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankDairyPrimaryDataRow } = useMutateFootprintingDairyPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankPigletPrimaryDataRow } = useMutateFootprintingPigletPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankPorkPrimaryDataRow } = useMutateFootprintingPorkPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankCropPrimaryDataRows } = useMutateFootprintingCropCreateManyBlankPrimaryData(
    farmId,
    carbonProgram.data?.farmLevelFootprinting?.programCrops ?? [],
    year
  );

  useEffect(() => {
    const allQueries = [...cropProgressQueries, beefProgressQuery, dairyProgressQuery, pigletProgressQuery, porkProgressQuery];
    const completedQueries = allQueries.filter((query) => isPercentageComplete(query.data?.completionPct ?? 0));
    const firstCompleteBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.FIRST);
    const multipleCompleteBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.MANY);
    const allCompleteBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.ALL);
    const submittedAssessmentsRequiringReviewBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.SUBMITTED_ASSESSMENTS);
    const unsubmittedAssessmentsRequiringReviewBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.UNSUBMITTED_ASSESSMENTS);

    if (completedQueries.length >= 1 && completedQueries.length === cropsWithFootprintForYear.length && getLocalKlimVar(allCompleteBannerKey) === null) {
      setLocalKlimVar(allCompleteBannerKey, "true");
      setBannerConfig(BANNER_CONFIGS.ALL_COMPLETE);
    } else if (completedQueries.length === 1 && getLocalKlimVar(firstCompleteBannerKey) === null) {
      setLocalKlimVar(firstCompleteBannerKey, "true");
      setBannerConfig(BANNER_CONFIGS.FIRST_COMPLETE);
    } else if (completedQueries.length > 1 && getLocalKlimVar(multipleCompleteBannerKey) === null) {
      setLocalKlimVar(multipleCompleteBannerKey, "true");
      setBannerConfig(BANNER_CONFIGS.MULTIPLE_COMPLETE);
    }

    if (submittedAssessmentsRequiringReviewForCurrentYear.length && getLocalKlimVar(submittedAssessmentsRequiringReviewBannerKey) === null) {
      setLocalKlimVar(submittedAssessmentsRequiringReviewBannerKey, "true");
      setSubmittedSplitBannerConfig(BANNER_CONFIGS.SUBMITTED_AFFECTED_BY_SPLIT);
    }

    if (unsubmittedAssessmentsRequiringReviewForCurrentYear.length) {
      setLocalKlimVar(unsubmittedAssessmentsRequiringReviewBannerKey, "true");
      setUnsubmittedSplitBannerConfig(BANNER_CONFIGS.UNSUBMITTED_AFFECTED_BY_SPLIT);
    }
  }, [
    cropsWithFootprintForYear.length,
    beefProgressQuery,
    dairyProgressQuery,
    pigletProgressQuery,
    porkProgressQuery,
    cropProgressQueries,
    farmId,
    year,
    unsubmittedAssessmentsRequiringReviewForCurrentYear,
    submittedAssessmentsRequiringReviewForCurrentYear,
  ]);

  const selectableCropIds = carbonProgram.data?.farmLevelFootprinting?.programCrops?.filter((cropId) => !cropsWithFootprintForYear.includes(cropId)) ?? [];
  const selectableBeef = beefEnabled && beefProgressQuery.data?.footprint === undefined;
  const selectableDairy = dairyEnabled && dairyProgressQuery.data?.footprint === undefined;
  const selectablePiglet = porkEnabled && pigletProgressQuery.data?.footprint === undefined;
  const selectablePork = porkEnabled && porkProgressQuery.data?.footprint === undefined;
  const selectableAssessments = [
    ...selectableCropIds,
    ...(beefEnabled ? ["beef"] : []),
    ...(dairyEnabled ? ["dairy"] : []),
    ...(porkEnabled ? ["piglet", "pork"] : []),
  ];

  const hasNoAssessmentsStarted =
    cropsWithFootprintForYear.length === 0 &&
    (beefEnabled ? beefProgressQuery.data?.footprint === undefined : true) &&
    (dairyEnabled ? dairyProgressQuery.data?.footprint === undefined : true) &&
    (porkEnabled ? pigletProgressQuery.data?.footprint === undefined && porkProgressQuery.data?.footprint === undefined : true);

  if (selectableAssessments.length > 0 && hasNoAssessmentsStarted) {
    return <AssessmentSelectionScreen farmId={farmId} year={year} onDidAutofill={() => setBannerConfig(BANNER_CONFIGS.AUTOFILL_SUCCESSFUL)} />;
  }

  const addAssessments = () => {
    setIsMutating(true);
    Promise.all([
      createBlankCropPrimaryDataRows({
        selectedCropIds: Object.entries(selectedCommodities)
          .filter(([_, isSelected]) => isSelected)
          .map(([cropId]) => cropId),
        autofillYear: year - 1,
      }),
      ...(selectedBeef ? [createBlankBeefPrimaryDataRow({})] : []),
      ...(selectedDairy ? [createBlankDairyPrimaryDataRow({})] : []),
      ...(selectedPiglet ? [createBlankPigletPrimaryDataRow({})] : []),
      ...(selectedPork ? [createBlankPorkPrimaryDataRow({})] : []),
    ]).then(() => {
      clearLocalKlimVar(bannerKey(farmId, year, "all"));
      setIsMutating(false);
      setSelectedBeef(false);
      setSelectedDairy(false);
      setSelectedPiglet(false);
      setSelectedPork(false);
      setSelectedCommodities({});
      setShowAddAssessmentModal(false);
    });
  };

  const handleReviewCropAssessments = () => {
    const assessmentsRequiringReviewPerCurrentYear = Object.keys(assessmentsRequiringReview?.[year] ?? {});
    if (assessmentsRequiringReviewPerCurrentYear.length) {
      setCropAssessmentToReview({ cropId: assessmentsRequiringReviewPerCurrentYear[0], step: 1 });
    }
  };

  return (
    <div>
      {isMutating || cropProgressQueries.some((cpq) => cpq.isLoading) ? (
        <FootprintingLoadingState cardType="farm" />
      ) : (
        <FootprintingFormButton cardType="farm" year={year} farmId={farmId} yearIsSubmitted={yearIsSubmitted} />
      )}
      <Flex className="margin-top-200" align="center" justify="space-between">
        <Typography variant="h3" color="grey-600">
          {t("assessment")}
        </Typography>
        {selectableAssessments.length > 0 ? (
          <IconButton iconName="klim/plus" onClick={() => setShowAddAssessmentModal(true)} size="small" type="tertiary" />
        ) : null}
      </Flex>
      <Flex>
        {!isAssessmentsRequiringReviewLoading && unsubmittedAssessmentsRequiringReviewForCurrentYear.length && unsubmittedSplitBannerConfig != null ? (
          <AlertBanner
            type={unsubmittedSplitBannerConfig.type}
            className="margin-top-100"
            iconName={unsubmittedSplitBannerConfig.iconName}
            iconSize={24}
            iconAlign="start"
            title={t(unsubmittedSplitBannerConfig.titleKey ?? "")}
            description={
              <Flex vertical gap={4}>
                <Typography variant="paragraph-m">{t(unsubmittedSplitBannerConfig.descriptionKey ?? "")}</Typography>
                <Flex gap={32}>
                  <Typography variant="paragraph-m" bold className="review-crop-assessments-button" onClick={handleReviewCropAssessments}>
                    {t(unsubmittedSplitBannerConfig.buttonTextKey ?? "")}
                  </Typography>
                </Flex>
              </Flex>
            }
          />
        ) : null}
        {!isAssessmentsRequiringReviewLoading && submittedAssessmentsRequiringReviewForCurrentYear.length && submittedSplitBannerConfig != null ? (
          <AlertBanner
            type={submittedSplitBannerConfig.type}
            className="margin-top-100"
            iconName={submittedSplitBannerConfig.iconName}
            iconSize={24}
            iconAlign="start"
            title={t(submittedSplitBannerConfig.titleKey ?? "")}
            description={t(submittedSplitBannerConfig.descriptionKey ?? "")}
            closeable
          />
        ) : null}
      </Flex>
      <Flex vertical className="margin-top-100">
        {bannerConfig !== null ? (
          <AlertBanner
            headingSize="h6"
            closeable
            iconSize={24}
            iconAlign="start"
            gap={4}
            iconName={bannerConfig.iconName}
            type={bannerConfig.type}
            title={bannerConfig.titleKey ? t(bannerConfig.titleKey) : undefined}
            description={t(bannerConfig.descriptionKey ?? "")}
            onClose={() => setBannerConfig(null)}
            link={"link" in bannerConfig ? bannerConfig.link : undefined}
            linkText={"linkTextKey" in bannerConfig && bannerConfig.linkTextKey ? t(bannerConfig.linkTextKey) : undefined}
            onClick={"hasClick" in bannerConfig && bannerConfig.hasClick ? () => setShowTaskOverviewDrawer(true) : undefined}
          />
        ) : null}
      </Flex>

      {isMutating || cropProgressQueries.some((cpq) => cpq.isLoading) ? (
        <>
          <FootprintingLoadingState cardType="crop" />
        </>
      ) : (
        <>
          {cropsWithFootprintForYear.map((cropId: string) => (
            <FootprintingFormButton
              key={`${year}-${cropId}`}
              cardType="crop"
              farmId={farmId}
              year={year}
              cropId={cropId}
              yearIsSubmitted={yearIsSubmitted}
              initialStep={cropAssessmentToReview?.cropId === cropId ? cropAssessmentToReview.step : undefined}
              onFormFlowClose={() => setCropAssessmentToReview(null)}
            />
          ))}
        </>
      )}
      {beefEnabled && beefProgressQuery.data?.footprint?.id ? (
        <FootprintingFormButton cardType="beef" farmId={farmId} year={year} yearIsSubmitted={yearIsSubmitted} />
      ) : null}
      {dairyEnabled && dairyProgressQuery.data?.footprint?.id ? (
        <FootprintingFormButton cardType="dairy" farmId={farmId} year={year} yearIsSubmitted={yearIsSubmitted} />
      ) : null}
      {porkEnabled && pigletProgressQuery.data?.footprint?.id ? (
        <FootprintingFormButton cardType="piglet" farmId={farmId} year={year} yearIsSubmitted={yearIsSubmitted} />
      ) : null}
      {porkEnabled && porkProgressQuery.data?.footprint?.id ? (
        <FootprintingFormButton cardType="pork" farmId={farmId} year={year} yearIsSubmitted={yearIsSubmitted} />
      ) : null}
      <ErrorBoundary>
        <TaskOverviewDrawer
          farmTasks={[]}
          visible={showTaskOverviewDrawer}
          onClose={() => setShowTaskOverviewDrawer(false)}
          onDataChange={() => {}}
          loading={false}
        />
        <Modal
          maskClosable
          showModal={showAddAssessmentModal}
          onCancel={() => setShowAddAssessmentModal(false)}
          onOk={addAssessments}
          okText={t("assessmentManagement.addAssessmentModal.button")}
          okButtonProps={{
            disabled:
              Object.values(selectedCommodities).filter(Boolean).length === 0 &&
              selectedBeef === false &&
              selectedDairy === false &&
              selectedPiglet === false &&
              selectedPork === false,
          }}
          cancelText={t("modal.cancel")}
          title={t("assessmentManagement.addAssessmentModal.title")}
          content={
            selectableCropIds.length > 0 || selectableBeef || selectableDairy || selectablePiglet || selectablePork ? (
              <Flex vertical className="padding-top-100" gap="1rem">
                {selectableCropIds.map((cropId) => {
                  const cropInfo = allCrops?.find((c) => c.id === cropId);
                  if (!cropInfo) return null;

                  return (
                    <SelectableImageCard
                      key={cropInfo.id}
                      selectionMode="multiple"
                      title={cropInfo.name}
                      titleVariant="paragraph-m"
                      checked={selectedCommodities[cropId]}
                      onChange={() => setSelectedCommodities({ ...selectedCommodities, [cropId]: !selectedCommodities[cropId] })}
                      icon={{
                        iconAlt: `${cropInfo.name} icon`,
                        iconName: CARD_TYPE_ICON.crop,
                      }}
                    />
                  );
                })}
                {selectableBeef ? (
                  <SelectableImageCard
                    key="selectable-beef"
                    selectionMode="multiple"
                    title={t("assessmentSelection.beef")}
                    titleVariant="paragraph-m"
                    checked={selectedBeef}
                    onChange={(e) => setSelectedBeef(!!e.target?.checked)}
                    icon={{
                      iconAlt: "Beef icon",
                      iconName: CARD_TYPE_ICON.beef,
                    }}
                  />
                ) : null}
                {selectableDairy ? (
                  <SelectableImageCard
                    key="selectable-dairy"
                    selectionMode="multiple"
                    title={t("assessmentSelection.dairy")}
                    titleVariant="paragraph-m"
                    checked={selectedDairy}
                    onChange={(e) => setSelectedDairy(!!e.target?.checked)}
                    icon={{
                      iconAlt: "Dairy icon",
                      iconName: CARD_TYPE_ICON.dairy,
                    }}
                  />
                ) : null}
                {selectablePiglet ? (
                  <SelectableImageCard
                    key="selectable-piglet"
                    selectionMode="multiple"
                    title={t("assessmentSelection.piglet")}
                    titleVariant="paragraph-m"
                    checked={selectedPiglet}
                    onChange={(e) => setSelectedPiglet(!!e.target?.checked)}
                    icon={{
                      iconAlt: "Piglet icon",
                      iconName: CARD_TYPE_ICON.piglet,
                    }}
                  />
                ) : null}
                {selectablePork ? (
                  <SelectableImageCard
                    key="selectable-pork"
                    selectionMode="multiple"
                    title={t("assessmentSelection.pork")}
                    titleVariant="paragraph-m"
                    checked={selectedPork}
                    onChange={(e) => setSelectedPork(!!e.target?.checked)}
                    icon={{
                      iconAlt: "Pork icon",
                      iconName: CARD_TYPE_ICON.pork,
                    }}
                  />
                ) : null}
              </Flex>
            ) : (
              <Flex vertical className="padding-200 " gap={24} align="center">
                <Icon className="grey-text" iconName="klim/crops-matured" size="xl" />
                <Typography variant="paragraph-m" color="grey-600" className="center">
                  {t("assessmentManagement.addAssessmentModal.noneAvailable")}
                </Typography>
              </Flex>
            )
          }
        />
      </ErrorBoundary>
    </div>
  );
};
